import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useQueryCache } from '@pinia/colada'
import { getAuth } from '@/plugins/auth'
import { useAuthPermissionsStore } from './auth-permissions.store'
import { clearAllCache } from '@/services/cache'
import { clearPartnerImpersonationTokens } from '@/services/api/partner-impersonation-cache'
import { clearLastReleases } from '@/services/releases/releases.storage'
import { clearTicketLists } from '@/services/ticket-list'
import { forgetMyWork } from '@/services/my-work/my-work.last-seen'
import { forgetSavedOpenDefault } from '@/services/open-in-claude-code/open-in-claude-code.read-once'
import { useAdminImpersonationService } from '@/services/admin-impersonation'
import { getErrorMessage } from '@/utils/error-handler'
import type {
  OriginalAdminInfo,
  ImpersonatedAdminInfo
} from '@/services/admin-impersonation'
import type { AuthUser } from '@/services/auth/auth.types'

export const useAuthStore = defineStore('adx-auth', () => {
  const queryCache = useQueryCache()
  const adminImpersonationService = useAdminImpersonationService()

  // === State ===
  const user = ref<AuthUser | null>(null)
  const accessToken = ref<string | null>(null)
  const tokenType = ref<string>('Bearer')
  const isImpersonating = ref(false)
  const originalAdmin = ref<OriginalAdminInfo | null>(null)
  const impersonatedAdmin = ref<ImpersonatedAdminInfo | null>(null)
  const originalAccessToken = ref<string | null>(null)
  const originalUser = ref<AuthUser | null>(null)
  const impersonationExpires = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // === Getters ===
  const isAuthenticated = computed(() => !!accessToken.value)
  const hasError = computed(() => error.value !== null)

  // === Actions ===

  /**
   * Load impersonation state from localStorage.
   * Normal auth is handled by Cognito (Amplify manages its own token storage).
   */
  const loadStoredImpersonation = () => {
    const storedImpersonation = localStorage.getItem('adx_impersonation')
    if (
      storedImpersonation &&
      storedImpersonation !== 'undefined' &&
      storedImpersonation !== 'null'
    ) {
      try {
        const impersonationData = JSON.parse(storedImpersonation)
        isImpersonating.value = true
        originalAdmin.value = impersonationData.originalAdmin
        impersonatedAdmin.value = impersonationData.impersonatedAdmin
        originalAccessToken.value = impersonationData.originalAccessToken
        originalUser.value = impersonationData.originalUser
        impersonationExpires.value = impersonationData.expires

        // Use the impersonation token as the active access token
        if (impersonationData.accessToken) {
          accessToken.value = impersonationData.accessToken
        }

        // Populate user from impersonation data
        if (impersonationData.impersonatedAdmin) {
          user.value = {
            id: impersonationData.impersonatedAdmin.userId,
            email: impersonationData.impersonatedAdmin.email,
            name: impersonationData.impersonatedAdmin.displayName
          }
        }

        // Check if impersonation has expired
        if (impersonationData.expires) {
          const expiresDate = new Date(impersonationData.expires)
          if (expiresDate < new Date()) {
            endImpersonationInternal()
          }
        }
      } catch {
        localStorage.removeItem('adx_impersonation')
      }
    }
  }

  // Internal method to end impersonation without API call
  const endImpersonationInternal = () => {
    // When ending impersonation, the Cognito session is still valid
    // accessToken will be refreshed from Cognito on next checkAuth
    isImpersonating.value = false
    originalAdmin.value = null
    impersonatedAdmin.value = null
    originalAccessToken.value = null
    originalUser.value = null
    impersonationExpires.value = null
    localStorage.removeItem('adx_impersonation')
  }

  /**
   * Check authentication by verifying the Cognito session.
   * Called on app load, route guards, and after callback.
   */
  const checkAuth = async () => {
    const permissionsStore = useAuthPermissionsStore()
    isLoading.value = true
    error.value = null

    try {
      // Load impersonation state first
      loadStoredImpersonation()

      // If impersonating, we already have a valid token from localStorage
      if (isImpersonating.value && accessToken.value) {
        if (!permissionsStore.isLoaded) {
          try {
            await permissionsStore.fetchMyPermissions()
          } catch {
            // Permissions failing shouldn't block auth — user can still navigate
          }
        }
        return true
      }

      // Check auth session via provider (Amplify or oidc-client-ts)
      try {
        const provider = getAuth()
        const session = await provider.getSession()

        if (session) {
          accessToken.value = session.accessToken
          user.value = session.user

          // Load permissions if not already loaded
          if (!permissionsStore.isLoaded) {
            try {
              await permissionsStore.fetchMyPermissions()
            } catch {
              // Permissions failing shouldn't block auth — user can still navigate
            }
          }

          return true
        }
      } catch {
        // No valid session
      }

      // No valid session found
      accessToken.value = null
      user.value = null
      return false
    } catch (e) {
      error.value = getErrorMessage(e, 'Authentication check failed')
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Get the current access token.
   * Returns impersonation token if active, otherwise Cognito token.
   */
  const getAccessToken = async (): Promise<string | undefined> => {
    if (isImpersonating.value && accessToken.value) {
      return accessToken.value
    }

    const provider = getAuth()
    return provider.getAccessToken()
  }

  const logout = async () => {
    const permissionsStore = useAuthPermissionsStore()
    isLoading.value = true
    error.value = null

    try {
      const provider = getAuth()
      await provider.signOut()
    } catch {
      // Sign out failure shouldn't block local cleanup
    } finally {
      // Clear local state regardless of sign out result
      user.value = null
      accessToken.value = null

      // Clear permissions
      permissionsStore.clearPermissions()

      // Clear Pinia Colada query cache (prevent stale data across sessions)
      clearAllCache(queryCache)

      // Drop minted partner-impersonation tokens — they outlive the session that
      // minted them, and must not carry over to whoever logs in next.
      clearPartnerImpersonationTokens()

      // Clear localStorage
      localStorage.removeItem('adx_access_token')
      localStorage.removeItem('adx_user')
      localStorage.removeItem('adx_impersonation')
      clearLastReleases()
      clearTicketLists()
      forgetMyWork()
      forgetSavedOpenDefault()

      isLoading.value = false
    }
  }

  /**
   * Start impersonating another administrator
   */
  const startImpersonation = async (
    administratorId: number,
    durationMinutes: number
  ): Promise<{ success: boolean; error?: string }> => {
    const permissionsStore = useAuthPermissionsStore()
    isLoading.value = true
    error.value = null

    try {
      const response = await adminImpersonationService.impersonateAdmin(
        administratorId,
        durationMinutes
      )

      // Store original token and user
      originalAccessToken.value = accessToken.value
      originalUser.value = user.value

      // Update to impersonated token and user
      accessToken.value = response.accessToken
      user.value = {
        id: response.admin.userId,
        email: response.admin.email,
        name: response.admin.displayName
      }

      // Set impersonation state
      isImpersonating.value = true
      originalAdmin.value = response.originalAdmin
      impersonatedAdmin.value = response.admin
      impersonationExpires.value = response.expires

      // Store in localStorage
      localStorage.setItem(
        'adx_impersonation',
        JSON.stringify({
          originalAdmin: response.originalAdmin,
          impersonatedAdmin: response.admin,
          originalAccessToken: originalAccessToken.value,
          originalUser: originalUser.value,
          accessToken: response.accessToken,
          expires: response.expires
        })
      )

      // Reload permissions for the impersonated admin
      try {
        await permissionsStore.fetchMyPermissions()
      } catch {
        // Permissions failing shouldn't block impersonation
      }

      return { success: true }
    } catch (e) {
      const message = getErrorMessage(e, 'Admin impersonation failed')
      error.value = message
      return { success: false, error: message }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * End the current impersonation session and restore original admin
   */
  const endImpersonation = async () => {
    const permissionsStore = useAuthPermissionsStore()
    isLoading.value = true
    error.value = null

    try {
      // Call API to log end of impersonation
      await adminImpersonationService.endImpersonation()
    } catch {
      // Continue with local cleanup even if API logging fails
    }

    try {
      // Restore original state — Cognito session is still valid
      endImpersonationInternal()

      // Refresh token from Cognito
      await checkAuth()

      // Reload permissions for the original admin
      try {
        await permissionsStore.fetchMyPermissions()
      } catch {
        // Permissions failing shouldn't block end-impersonation
      }
    } catch (e) {
      error.value = getErrorMessage(e, 'Failed to end impersonation')
      throw e
    } finally {
      isLoading.value = false
    }
  }

  const clearError = () => {
    error.value = null
  }

  const reset = () => {
    user.value = null
    accessToken.value = null
    tokenType.value = 'Bearer'
    isImpersonating.value = false
    originalAdmin.value = null
    impersonatedAdmin.value = null
    originalAccessToken.value = null
    originalUser.value = null
    impersonationExpires.value = null
    isLoading.value = false
    error.value = null
  }

  return {
    // State
    user,
    accessToken,
    tokenType,
    isLoading,
    error,

    // Impersonation state
    isImpersonating,
    originalAdmin,
    impersonatedAdmin,
    impersonationExpires,

    // Computed
    isAuthenticated,
    hasError,

    // Actions
    logout,
    checkAuth,
    getAccessToken,
    startImpersonation,
    endImpersonation,
    clearError,
    reset
  }
})
